import { inject } from "@angular/core";
import {
  CanActivateFn,
  Router,
  UrlTree,
} from "@angular/router";

import { ErrorPageCode } from "../../app.enums";
import { ApplicationFactory } from "../../factories/ApplicationFactory";
import { GenericErrorHandler } from "../../services/error-handlers.service";

import {
  ActivityIndicatorService,
  ConversationDestinationEnum,
  DeviceService,
  ThemeService,
} from "../../../core-ui";
import { ApplicationService } from "../../../core-ui/services/application.service";
import { AuthState } from "../../../core-ui/models/AuthState";
import { discoverApplicationId } from "../../app.utils";
import { LocaleService } from "../../../app/services";

export const appAgentGuard: CanActivateFn = async (
  route,
  state,
): Promise<boolean | UrlTree> => {
  const applicationService = inject(ApplicationService);
  const applicationFactory = inject(ApplicationFactory);
  const themeService = inject(ThemeService);
  const floatingService = inject(ActivityIndicatorService);
  const errorHandler = inject(GenericErrorHandler);
  const deviceService = inject(DeviceService);
  const localeService = inject(LocaleService);
  const router = inject(Router);

  if (applicationService.getActiveApplication()) {
    return true;
  }

  try {
    floatingService.loading(true, "", true); // "initializing application"
    const applicationId = discoverApplicationId(route.queryParamMap);

    if (
      !applicationId &&
      route.url[0].path !== "apps" &&
      route.queryParamMap.has("state")
    ) {
      const astate = AuthState.fromSerializedState(
        route.queryParamMap.get("state"),
      );
      // redirect to applications page. The agent will select an integration in order to get the
      // application id and the continue with the flow.
      if (
        astate?.conversationDestination ===
        ConversationDestinationEnum.MOBILE_OFFICE
      ) {
        return router.createUrlTree(["/apps"], {
          queryParams: route.queryParams,
        });
      }
    }

    const application = await applicationFactory.createAgentApplication(
      applicationId,
      route,
    );
    applicationService.setActiveApplication(application);
    themeService.parseApplicationOptions(application);
    localeService.parseApplicationOptions(application.getConfig());
    deviceService.init();

    return true;
  } catch (error) {
    errorHandler.handleError(error);
    return router.createUrlTree([
      "/error",
      ErrorPageCode.AUTHENTICATION_FAILURE,
    ]);
  } finally {
    floatingService.loading(false);
  }
};
